import React, { useEffect, useContext } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router';
import AppContext from 'Utils/AppContext';
import ProdToggle from './ProdToggle';

const SnowflakeSourceEditor = ({ connection, setConnection }) => {
  const { rulesList } = useContext(AppContext);
  const { id } = useParams();

  const defaultRules = rulesList.find(rules => rules.default === true);

  // FIELDS = host/database/schema/warehouse; JDBC_URL = single URL string.
  // Username/password show in both. Initial mode is inferred by toUiDataSource.
  const connectionMode = connection._connectionMode || 'FIELDS';
  const isJdbcUrlMode = connectionMode === 'JDBC_URL';
  const setConnectionMode = (mode) => setConnection({ ...connection, _connectionMode: mode });

  const defaults = {
    database: '',
    host: '',
    name: '',
    password: '',
    port: '',
    user: '',
    type: 'snowflake',
    warehouse: '',
    schemaName: '',
    ...!id && ({
      scheduleEnabled: true,
      scheduleDayOfWeek: 'SAT',
      scheduleWeekOccurrence: 1,
      scheduleHour: 0,
      scheduleMinute: 0,
      scheduleRules: defaultRules
    })
  }

  // Merge defaults UNDER any fields the parent SourceForm dropdown already populated
  // (type / customDatabaseType / port). A plain `setConnection(defaults)` would wipe
  // those out and break SourceForm's option lookup, hiding the editor entirely.
  useEffect(() => { if (!id) setConnection((prev) => ({ ...defaults, ...(prev || {}) })); }, []);
  
  return (
    <div className='text-left'>
      <Row className='mb-3'>
        <Col>
          <Form.Label>Data source name *</Form.Label>
          <Form.Control
            size='sm'
            placeholder='Sample data source'
            value={connection.name}
            onChange={(n) => setConnection({ ...connection, name: n.target.value })}
            type='text'
            required
            isInvalid={connection && connection.name && connection.name.includes(' ')}
          />
          <Form.Control.Feedback type="invalid">
            Data source names must not include spaces.
          </Form.Control.Feedback>
        </Col>
      </Row>
      <ProdToggle />
      <Row className='mb-3'>
        <Col lg='12'>
          <Form.Label>Connection details</Form.Label>
          <div>
            <Form.Check
              inline
              type='radio'
              id='sf-conn-mode-fields'
              name='sf-conn-mode'
              label='Account, database, warehouse'
              checked={!isJdbcUrlMode}
              onChange={() => setConnectionMode('FIELDS')}
            />
            <Form.Check
              inline
              type='radio'
              id='sf-conn-mode-jdbc'
              name='sf-conn-mode'
              label='JDBC URL'
              checked={isJdbcUrlMode}
              onChange={() => setConnectionMode('JDBC_URL')}
            />
          </div>
          <Form.Text className='text-muted'>
            {isJdbcUrlMode
              ? 'Provide a complete Snowflake JDBC URL. Username and password below are still applied at connect time.'
              : 'Pick this for a standard Snowflake account. Switch to JDBC URL when you need to set per-connection params (role, authenticator, key-pair auth, etc.) the form fields don\u2019t cover.'}
          </Form.Text>
        </Col>
      </Row>

      {isJdbcUrlMode ? (
        <Row className='mb-3'>
          <Col lg='12'>
            <Form.Label>JDBC URL *</Form.Label>
            <Form.Control
              size='sm'
              type='text'
              value={connection.jdbcUrl ?? ''}
              onChange={(n) => setConnection({ ...connection, jdbcUrl: n.target.value })}
              placeholder='jdbc:snowflake://xy12345.us-east-1.snowflakecomputing.com/?db=SAMPLEDB&warehouse=COMPUTE_WH&schema=PUBLIC'
              required
            />
            <Form.Text className='text-muted'>
              Used verbatim. The driver is still chosen by the data source type selector above.
            </Form.Text>
          </Col>
        </Row>
      ) : (
        <>
          <Row className='mb-3'>
            <Col lg='12' className='pr-1'>
              <Form.Label>Host *</Form.Label>
              <Form.Control
                size='sm'
                placeholder='xy12345.us-east-1.snowflakecomputing.com'
                value={connection.host || ''}
                onChange={(n) => setConnection({ ...connection, host: n.target.value })}
                type='text'
                required
              />
            </Col>
          </Row>
          <Row className='mb-3'>
            <Col lg='12'>
              <Form.Label>Database *</Form.Label>
              <Form.Control
                size='sm'
                value={connection.database || ''}
                onChange={(n) => setConnection({ ...connection, database: n.target.value })}
                type='text'
                placeholder='SAMPLEDATABASE'
                required
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col lg='6' className='pr-1'>
              <Form.Label>Schema *</Form.Label>
              <Form.Control
                size='sm'
                value={connection.schemaName || ''}
                onChange={(n) => setConnection({ ...connection, schemaName: n.target.value })}
                type='text'
                placeholder='PUBLIC'
                required
              />
            </Col>
            <Col lg='6' className='pl-1'>
              <Form.Label>Warehouse *</Form.Label>
              <Form.Control
                size='sm'
                value={connection.warehouse || ''}
                onChange={(n) => setConnection({ ...connection, warehouse: n.target.value })}
                type='text'
                placeholder='COMPUTE_WH'
                required
              />
            </Col>
          </Row>
        </>
      )}

      <Row className='mb-3'>
        <Col lg='6' className='pr-1'>
          <Form.Label>Username *</Form.Label>
          <Form.Control
            size='sm'
            type='text'
            value={connection.user}
            onChange={(n) => setConnection({ ...connection, user: n.target.value})}
            placeholder='Username'
            required
            autoComplete='new-password'
          />
        </Col>
        <Col lg='6' className='pl-1'>
          <Form.Label>Password{id ? '' : ' *'}</Form.Label>
          <Form.Control
            size='sm'
            type='password'
            value={connection.password ?? ''}
            placeholder={id ? 'Leave blank to keep current; enter new password to change' : 'Password'}
            onChange={(n) => setConnection({ ...connection, password: n.target.value })}
            required={!id}
            autoComplete={id ? 'off' : 'new-password'}
          />
        </Col>
      </Row>

      {/* <Row className='mb-3'>
        <Col>
          <CronJobEditor 
            scheduleExpression={connection.scheduleExpression}
            scheduleRules={connection.scheduleRules}
            setScheduleData={(v) => setConnection({ ...connection, scheduleExpression: v.scheduleExpression, scheduleRules: v.scheduleRules })}
          />
        </Col>
      </Row> */}
    </div>
  );
}

export default SnowflakeSourceEditor;
